import { formatCompanyTime } from "@/lib/companyTime";
import { useState, useRef, useEffect } from "react";
import IconButton from "@/components/common/GoogleIconButton";
import Button from "@mui/material/Button";
import { Bell, CheckCheck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  useUnreadInsightCount,
  useAgentInsights,
  useAcknowledgeInsight,
  useAcknowledgeAllInsights,
} from "../hooks/useAgents";
import type { AgentInsight } from "../types/agent";
import { usePendingAction } from "@/hooks/usePendingAction";

export function AgentNotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data: unreadCount } = useUnreadInsightCount();
  const { data: recentInsights } = useAgentInsights({
    acknowledged: false,
    limit: 5,
  });
  const acknowledge = useAcknowledgeInsight();
  const acknowledgeAll = useAcknowledgeAllInsights();
  const { isPending: markingAll, run: runMarkAll } = usePendingAction();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const count = unreadCount ?? 0;
  const insights = recentInsights?.insights ?? [];

  return (
    <div className="relative" ref={ref}>
      <IconButton
        size="small"
        aria-label="Agent insights notifications"
        aria-expanded={open}
        className="relative h-7 w-7 sm:h-8 sm:w-8"
        sx={{
          padding: 0,
          borderRadius: "0.75rem",
          border: "1px solid var(--border-40)",
          backgroundColor: "var(--muted-25)",
          color: "var(--muted-foreground)",
          "&:hover": {
            borderColor: "var(--border-60)",
            backgroundColor: "var(--foreground-5)",
            color: "var(--foreground)",
          },
        }}
        onClick={() => setOpen(!open)}
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold leading-none text-destructive-foreground">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </IconButton>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-popover border border-border rounded-xl shadow-elevated z-50">
          <div className="flex items-center justify-between p-3 border-b">
            <span className="text-sm font-medium">Agent Insights</span>
            <Button
              variant="text"
              size="small"
              sx={{ minWidth: 0 }}
              onClick={() => {
                navigate("/agents");
                setOpen(false);
              }}
            >
              View All
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {insights.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No new insights
              </div>
            ) : (
              insights.map((insight: AgentInsight) => (
                <NotificationItem
                  key={insight.id}
                  insight={insight}
                  onDismiss={() => {
                    return acknowledge.mutateAsync(insight.id);
                  }}
                  onClick={() => {
                    if (insight.action_url) {
                      navigate(insight.action_url);
                    } else {
                      navigate("/agents");
                    }
                    setOpen(false);
                  }}
                />
              ))
            )}
          </div>
          {count > 0 && insights.length > 0 && (
            <div className="border-t">
              <Button
                fullWidth
                variant="text"
                size="small"
                disabled={markingAll}
                loading={markingAll}
                loadingPosition="start"
                aria-busy={markingAll}
                onClick={() =>
                  void runMarkAll(async () => {
                    try {
                      await acknowledgeAll.mutateAsync(undefined, {
                        onSuccess: (n) =>
                          n > 0 &&
                          toast.success(
                            `${n} insight${n === 1 ? "" : "s"} marked as read`,
                          ),
                      });
                    } catch {
                      toast.error(
                        "Could not mark insights as read. Please try again.",
                      );
                    }
                  })
                }
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1" />
                Mark all as read
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationItem({
  insight,
  onDismiss,
  onClick,
}: {
  insight: AgentInsight;
  onDismiss: () => Promise<unknown>;
  onClick: () => void;
}) {
  const { isPending, run } = usePendingAction();
  const severityColor =
    insight.severity === "critical"
      ? "bg-destructive"
      : insight.severity === "warning"
        ? "bg-warning"
        : "bg-info";

  return (
    <div className="flex items-start gap-1 border-b border-border/50 last:border-0">
      <button
        type="button"
        onClick={onClick}
        aria-label={`Open ${insight.title}`}
        className="flex min-w-0 flex-1 items-start gap-3 rounded-lg p-3 text-left hover:bg-muted/50 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2"
      >
        <span
          className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${severityColor}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-medium truncate">
            {insight.title}
          </span>
          <span className="block text-[10px] text-muted-foreground line-clamp-1">
            {insight.description}
          </span>
          <span className="block text-[10px] text-muted-foreground">
            {formatCompanyTime(insight.created_at)}
          </span>
        </span>
      </button>
      <IconButton
        type="button"
        size="small"
        aria-label="Dismiss insight"
        className="mt-2 mr-2 shrink-0"
        disabled={isPending}
        loading={isPending}
        aria-busy={isPending}
        sx={{ padding: 0 }}
        onClick={(e) => {
          e.stopPropagation();
          void run(async () => {
            try {
              await onDismiss();
            } catch {
              toast.error("Could not dismiss the insight. Please try again.");
            }
          });
        }}
      >
        <X className="h-3.5 w-3.5" />
      </IconButton>
    </div>
  );
}
